import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, initAnalytics, trackEvent, VSCODE_EXTENSION_URL, watchAuthState } from './firebase';
import { verificarSuscripcionPaypal, cancelarSuscripcionPaypal } from './api';
import { translate, getStoredLanguage, setStoredLanguage } from './i18n';
import { getStoredTheme, useTheme } from './theme';
import LanguageToggle from './LanguageToggle';
import './SubscribePage.css';

const PAYPAL_CLIENT_ID = 'BAAXSzCI9DguDE6Exe43hA5Zo_VwG-L3owYm3_To3SY9m2a-K0BU3GRHlKzG_q90Ac4ibTzQwkPhyu3hSw';

// Kept in sync by hand with functions/main.py's PLAN_ID_TO_TIER and
// backend/app/services/tiers.py's TIER_LIMITS.
const PLANS = [
  { tier: 'basico', planId: 'P-8YC60148FP9070923NKSJARI', nameKey: 'planBasicoName', price: '9' },
  { tier: 'pro', planId: 'P-7CW875121M512011GNKSJBTQ', nameKey: 'planProName', price: '20' },
  // Web + VS Code extension, no limits.
  { tier: 'coders', planId: 'P-5T7389049X0731258NLAVT7I', nameKey: 'planCodersName', price: '20' },
  { tier: 'premium', planId: 'P-0YX46796W9517553BNKSJCTY', nameKey: 'planPremiumName', price: '40' }
];

const RETIRED_TIERS = ['pro'];

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 18, height: 18, flexShrink: 0 }}>
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

export default function SubscribePage() {
  const navigate   = useNavigate();
  const btnRefs    = { basico: useRef(null), pro: useRef(null), coders: useRef(null), premium: useRef(null) };
  const [language, setLanguage] = useState(() => getStoredLanguage());
  const t = (key) => translate(language, key);
  useTheme(getStoredTheme());
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setStoredLanguage(lang);
  };

  const featuresFor = (tier) => {
    if (tier === 'basico') {
      return [t('featureBasicoMessages'), t('featureBasicoTextOnly')];
    }
    if (tier === 'coders') {
      return [
        t('featureNoLimits'),
        t('featureVscode'),
        t('featureLongReplies'),
        t('featureVoiceVision'),
        t('featureMorePremiumAttachments'),
        t('featureWorkspacesEverywhere')
      ];
    }
    const base = [t('featureUnlimitedMessages'), t('featureVoiceVision'), t('featureMoreContext')];
    if (tier === 'premium') {
      return [
        ...base,
        t('featureVscode'),
        t('featureLongReplies'),
        t('featureMorePremiumAttachments'),
        t('featurePrioritySupport')
      ];
    }
    return [...base, t('featurePriorityAccess')];
  };

  const [sdkReady,  setSdkReady]  = useState(false);
  const [error,     setError]     = useState('');
  const [subStatus, setSubStatus] = useState('loading'); // 'loading' | 'active' | 'none'
  const [currentTier, setCurrentTier] = useState(null);
  // Plus ('pro') is no longer sold - its card only stays for an existing
  // Plus subscriber, so they can still see and cancel their plan. The tier
  // itself keeps working (backend/app/services/tiers.py, functions/main.py).
  const visiblePlans = PLANS.filter((p) => !RETIRED_TIERS.includes(p.tier) || p.tier === currentTier);
  const [subId,     setSubId]     = useState('');
  const [showCancelPanel, setShowCancelPanel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  /* Check current subscription status - waits for watchAuthState rather than
     reading auth.currentUser synchronously, since right after a page
     refresh Firebase hasn't finished restoring the persisted session yet
     (currentUser is briefly null), which was wrongly read as "not signed
     in" and left the page stuck showing "no subscription". */
  useEffect(() => {
    // Idempotent - safe to call again even if App.js already initialized it
    // (e.g. a direct link straight to /subscribe never mounts App.js at all).
    initAnalytics();
  }, []);

  useEffect(() => {
    const unsubscribe = watchAuthState((user) => {
      if (!user) { setSubStatus('none'); return; }
      getDoc(doc(db, 'users', user.uid))
        .then(d => {
          if (d.exists() && d.data().subscribed === true) {
            setSubStatus('active');
            setCurrentTier(d.data().tier || 'pro');
            setSubId(d.data().subscriptionId || '');
          } else {
            setSubStatus('none');
          }
        })
        .catch(() => setSubStatus('none'));
    });
    return unsubscribe;
  }, []);

  /* Load PayPal SDK once */
  useEffect(() => {
    const existing = document.getElementById('paypal-sdk');
    if (existing) {
      // Script tag already in DOM (e.g. hot reload) — wait until window.paypal is ready
      if (window.paypal) {
        setSdkReady(true);
      } else {
        existing.addEventListener('load', () => setSdkReady(true));
      }
      return;
    }
    const script = document.createElement('script');
    script.id  = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.setAttribute('data-sdk-integration-source', 'button-factory');
    script.onload  = () => setSdkReady(true);
    script.onerror = () => setError(t('errPaypalLoad'));
    document.body.appendChild(script);
    // Runs once on mount only; t() is read fresh inside the onerror
    // callback whenever it actually fires, so it doesn't need to be a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Render one PayPal button per plan once the SDK is ready - only shown
     when the user has no active subscription at all, to avoid ending up
     with two simultaneous PayPal subscriptions. */
  useEffect(() => {
    // The subscription check (see the effect above) resolves async, so this
    // can render buttons into a tier's container while subStatus briefly
    // reads "none" during a page refresh's initial state, just before it
    // settles to "active" - and since PayPal's SDK injects real DOM nodes
    // outside React's own diffing, switching that tier's card to the
    // "already subscribed" JSX doesn't clean those nodes up by itself.
    // Clearing the current tier's container here (independent of the early
    // return below) guarantees it never keeps showing stale buttons.
    if (subStatus === 'active' && currentTier && btnRefs[currentTier]?.current) {
      btnRefs[currentTier].current.innerHTML = '';
    }

    if (!sdkReady || subStatus !== 'none' || !window.paypal) return;

    const auth = getAuth();

    PLANS.forEach(({ tier, planId }) => {
      const container = btnRefs[tier].current;
      if (!planId || !container || container.childElementCount > 0) return;

      window.paypal.Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'subscribe' },
        // custom_id ties the subscription to this account - functions/main.py
        // refuses to grant it to any other uid.
        createSubscription: (_data, actions) => actions.subscription.create({
          plan_id: planId,
          custom_id: auth.currentUser?.uid
        }),
        onApprove: async (data) => {
          const user = auth.currentUser;
          if (!user) { setError(t('errSessionExpired')); return; }
          try {
            await verificarSuscripcionPaypal({ subscriptionID: data.subscriptionID });
            trackEvent('subscribe', { tier, subscription_id: data.subscriptionID });
            navigate('/');
          } catch (err) {
            console.error(err);
            setError(t('errPaypalApprovedButFailed'));
          }
        },
        onError: (err) => {
          console.error(err);
          setError(t('errPaypalGeneric'));
        },
      }).render(container);
    });
    // t() is read fresh inside the PayPal callbacks whenever they fire;
    // adding it here would just re-run this (idempotent, guarded) effect
    // on every render since t isn't memoized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkReady, subStatus, currentTier, navigate]);

  const handleCancelSubscription = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) { setCancelError(t('errSessionExpired')); return; }

    setCancelling(true);
    setCancelError('');

    try {
      await cancelarSuscripcionPaypal();
      setSubStatus('none');
      setCurrentTier(null);
      setShowCancelPanel(false);
    } catch (err) {
      console.error(err);
      setCancelError(t('errCancelSubscription'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="sub-page">
      <header className="sub-page-header">
        <div className="sub-page-titlebar">
          <div className="sub-page-titlebar-start">
            <button
              className="sub-back-icon-btn"
              type="button"
              onClick={() => navigate('/')}
              aria-label={t('backAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="sub-page-title">{t('subscribeTitle')}</h1>
          </div>
          <LanguageToggle language={language} onChange={handleLanguageChange} />
        </div>
      </header>

      <main className="sub-page-main">
        {subStatus === 'loading' && (
          <div className="sub-status-loading">{t('loadingEllipsis')}</div>
        )}

        {subStatus !== 'loading' && (
          <>
            <div className="sub-hero">
              {subStatus === 'active' ? (
                <>
                  <span className="sub-badge sub-badge-active">{t('activeBadge')}</span>
                  <h1>{t(PLANS.find((p) => p.tier === currentTier)?.nameKey || 'planProName')}</h1>
                  <p>{t('proPlanActiveDesc')}</p>
                </>
              ) : (
                <>
                  <span className="sub-badge">{t('requiredBadge')}</span>
                  <h1>{t('subscriptionRequiredTitle')}</h1>
                  <p>{t('subscriptionRequiredDesc')}</p>
                </>
              )}
            </div>

            <div className="sub-cards">
              {visiblePlans.map(({ tier, planId, nameKey, price }) => {
                const isCurrent = subStatus === 'active' && currentTier === tier;
                return (
                  <div key={tier} className={`sub-card${tier === 'pro' ? ' sub-card-pro' : ''}`}>
                    {tier === 'pro' && <div className="sub-card-badge">{t('mostPopular')}</div>}
                    {tier === 'coders' && <div className="sub-card-badge">{t('forDevelopers')}</div>}
                    <div className="sub-card-header">
                      <h2>{t(nameKey)}</h2>
                      <div className="sub-price">
                        <span className="sub-amount">${price}</span>
                        <span className="sub-period">{t('perMonth')}</span>
                      </div>
                    </div>
                    <ul className="sub-features">
                      {featuresFor(tier).map((f) => (
                        <li key={f}><CheckIcon /> {f}</li>
                      ))}
                    </ul>
                    {tier === 'coders' && (
                      <a className="sub-extension-link" href={VSCODE_EXTENSION_URL} target="_blank" rel="noreferrer">
                        {t('vscodeExtensionLink')} →
                      </a>
                    )}

                    {isCurrent ? (
                      <>
                        <div className="sub-active-status">
                          <CheckIcon />
                          {t('activeSubscription')}
                        </div>
                        {subId && <p className="sub-cancel-note">ID: {subId}</p>}
                        <button
                          className="sub-current-btn"
                          type="button"
                          style={{ marginTop: 16 }}
                          onClick={() => navigate('/')}
                        >
                          {t('backToChat')}
                        </button>
                        {!showCancelPanel ? (
                          <button
                            className="sub-cancel-link-btn"
                            type="button"
                            onClick={() => setShowCancelPanel(true)}
                          >
                            {t('cancelSubscription')}
                          </button>
                        ) : (
                          <div className="sub-cancel-panel">
                            <p>{t('cancelConfirmText')}</p>
                            <button
                              className="sub-cancel-confirm-btn"
                              type="button"
                              onClick={handleCancelSubscription}
                              disabled={cancelling}
                            >
                              {cancelling ? t('cancelling') : t('yesCancelSubscription')}
                            </button>
                            <button
                              className="sub-cancel-dismiss-btn"
                              type="button"
                              onClick={() => { setShowCancelPanel(false); setCancelError(''); }}
                              disabled={cancelling}
                            >
                              {t('backAria')}
                            </button>
                            {cancelError && (
                              <p className="sub-error">
                                {cancelError} {t('cancelErrorSuffix')}{' '}
                                <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">
                                  {t('yourPaypalAccount')}
                                </a>.
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : subStatus === 'active' ? (
                      <p className="sub-cancel-note">{t('cancelToSwitchPlan')}</p>
                    ) : (
                      <>
                        <div ref={btnRefs[tier]} className="sub-paypal-sdk-btn" />
                        {!planId && <p className="sub-paypal-done-note">{t('comingSoon')}</p>}
                        {planId && !sdkReady && !error && (
                          <p className="sub-paypal-done-note">{t('loadingPaypal')}</p>
                        )}
                        <p className="sub-cancel-note">{t('cancelAnytimeNote')}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {error && <p className="sub-error">{error}</p>}
          </>
        )}
      </main>
    </div>
  );
}
